import { defineStore } from 'pinia'

/**
 * Global Client Store
 * 
 * This store manages the current client context throughout the application.
 * It provides reactive access to client data and client-specific functionality.
 * 
 * Usage in components:
 * ```javascript
 * import { useClientStore } from '@/stores/client'
 * 
 * export default {
 *   setup() {
 *     const clientStore = useClientStore()
 *     return { clientStore }
 *   }
 * }
 * ```
 * 
 * Available getters:
 * - clientStore.client - Get current client object
 * - clientStore.isLoaded - Check if client is loaded
 * - clientStore.getClientUrl - Get client URL
 * - clientStore.getClientName - Get client name
 * 
 * Available actions:
 * - clientStore.setClient(clientData) - Set current client
 * - clientStore.clearClient() - Clear client data
 * - clientStore.updateClient(updates) - Update client data
 */

export const useClientStore = defineStore('client', {
    state: () => ({
        client: null,
        isLoaded: false,
        permissions: {}, // Client-specific permissions
        tasks: [], // Client tasks moved from lockstep store
        /** Full client list for admin nav (OrgSelect). Used only when user is admin on admin pages so the nav stays consistent. */
        allClients: [],
    }),
    
    getters: {
        // Get current client
        getClient: (state) => state.client,
        
        // Check if client is loaded
        isClientLoaded: (state) => state.isLoaded && state.client !== null,
        
        // Get client URL
        getClientUrl: (state) => state.client?.url || null,
        
        // Get client name
        getClientName: (state) => state.client?.name || '',
        
        // Get client ID
        getClientId: (state) => state.client?.id || null,
        
        // Get client plan level
        getClientPlanLevel: (state) => state.client?.plan_level || null,
        
        // Get client referring agency
        getReferringAgency: (state) => state.client?.referring_agency || null,
        
        // Get client referring agency ID
        getReferringAgencyId: (state) => state.client?.referring_agency_id || null,
        
        // Check if client has specific feature
        hasFeature: (state) => (feature) => {
            if (!state.client) return false;
            return state.client.features?.includes(feature) || false;
        },
        
        // Check if client has API key
        hasApiKey: (state) => {
            return !!(state.client?.census_api_key || state.client?.api_key);
        },
        
        // Get client permissions
        getClientPermissions: (state) => state.permissions,

        // ----- Task getters -----
        getTasks: (state) => state.tasks,
        
        // Check if user has permission for current client
        hasPermission: (state) => (section, action) => {
            if (!state.permissions || !state.permissions[section]) {
                return false;
            }
            return state.permissions[section][action] === true;
        },
        
        // Check if user can view section
        canView: (state) => (section) => {
            return state.permissions?.[section]?.view === true;
        },
        
        // Check if user can edit section
        canEdit: (state) => (section) => {
            return state.permissions?.[section]?.edit === true;
        },
        
        // Check if user can export from section
        canExport: (state) => (section) => {
            return state.permissions?.[section]?.export === true;
        }
    },
    
    actions: {
        // Initialize client data
        initialize(clientData, permissions = {}, tasks = []) {
            this.client = clientData;
            this.permissions = permissions;
            this.tasks = tasks;
            this.isLoaded = true;
        },
        
        // Set current client
        setClient(clientData) {
            this.client = clientData;
            if(clientData?.tasks) {
                this.setTasks(clientData.tasks);
            }
            this.isLoaded = true;
        },
        
        // Update client data
        updateClient(updates) {
            if (this.client) {
                this.client = { ...this.client, ...updates };
            }
        },
        
        // Set client permissions
        setPermissions(permissions) {
            this.permissions = permissions;
        },
        
        // Clear client data
        clearClient() {
            this.client = null;
            this.permissions = {};
            this.tasks = [];
            this.isLoaded = false;
        },

        /**
         * Set the full client list for admin org selector (nav).
         * Only used when user is admin; ensures OrgSelect shows the same list on all admin pages.
         */
        setAllClients(clients) {
            this.allClients = Array.isArray(clients) ? clients : [];
        },
        
        // Switch to different client
        switchClient(clientData, permissions = {}) {
            this.client = clientData;
            this.permissions = permissions;
            this.isLoaded = true;
        },
        
        // Refresh client data from server
        async refreshClient() {
            if (!this.client?.id) return;
            
            try {
                const response = await fetch(`/api/clients/${this.client.id}`);
                if (response.ok) {
                    const clientData = await response.json();
                    this.updateClient(clientData);
                }
            } catch (error) {
                console.error('Failed to refresh client data:', error);
            }
        },
        
        // Get client-specific URL
        getUrl(path = '') {
            if (!this.client?.url) return path;
            return `/${this.client.url}${path ? '/' + path : ''}`;
        },
        
        // Get client-specific API URL
        getApiUrl(endpoint = '') {
            if (!this.client?.url) return endpoint;
            return `/api/${this.client.url}${endpoint ? '/' + endpoint : ''}`;
        },

        // ----- Task actions -----
        setTasks(tasks = []) {
            this.tasks = tasks;
        },

        async fetchTasks() {
            if (!this.client?.url && !this.client?.is_onboarding || this.client?.url === 'demo') return;
            try {
                const response = await fetch(`/api/task/${this.client.url}`);
                if (response.ok) {
                    const data = await response.json();
                    this.setTasks(data.tasks || data);
                }
            } catch (e) {
                console.error('Failed to fetch tasks', e);
            }
        },

        updateTask(updatedTask) {
            const existingIndex = this.tasks.findIndex(task => task.id === updatedTask.id);
            if (existingIndex !== -1) {
                // Update existing task
                this.tasks[existingIndex] = { ...this.tasks[existingIndex], ...updatedTask };
            }
        },
    }
})
